import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { playTicketChime } from "@/lib/sound";
import { playCelebration, stopCelebrationSound } from "@/lib/celebrationSounds";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, X, LifeBuoy, PartyPopper, MessageCircle } from "lucide-react";
import { fmtEUR } from "@/components/sales/DealPieces";

const POLL_MS = 12000;
const CHANNEL_LABEL = { email: "E-mail", circle: "Circle", circle_dm: "Circle DM", whatsapp: "WhatsApp" };

export default function NotificationCenter() {
  const navigate = useNavigate();
  const sinceRef = useRef(null);
  const [wonDeal, setWonDeal] = useState(null);
  const [pops, setPops] = useState([]);

  const fireConfetti = useCallback(() => {
    const end = Date.now() + 2800;
    const colors = ["#0b1f4d", "#10b981", "#fbbf24", "#ffffff"];
    (function frame() {
      confetti({ particleCount: 5, angle: 60, spread: 60, startVelocity: 55, origin: { x: 0, y: 0.65 }, colors });
      confetti({ particleCount: 5, angle: 120, spread: 60, startVelocity: 55, origin: { x: 1, y: 0.65 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, startVelocity: 45, origin: { y: 0.5 }, colors });
  }, []);

  const celebrate = useCallback((deals, meta) => {
    const best = [...deals].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    setWonDeal(best);
    fireConfetti();
    // Zelfde volle feestgeluid als de TV (per-deal track of het geconfigureerde deal-geluid);
    // respecteert de geluidsknop in de koptekst — report:false zodat een academy-browser
    // met autoplay-blokkade geen "TV-geluid kapot"-alarm naar Slack stuurt.
    const sndCfg = best?.sound ? { ...best.sound, enabled: true } : meta?.sounds?.deal;
    playCelebration(sndCfg, "deal", meta?.tune_url || null, 8000, { report: false });
  }, [fireConfetti]);

  const pushPops = useCallback((items) => {
    if (!items.length) return;
    setPops((prev) => [...items, ...prev].slice(0, 4));
    playTicketChime();
    items.forEach((t) =>
      setTimeout(() => setPops((p) => p.filter((x) => x._k !== t._k)), 14000)
    );
  }, []);

  const poll = useCallback(async () => {
    try {
      const url = `/notifications/feed${sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : ""}`;
      const { data } = await api.get(url);
      const baseline = sinceRef.current === null;
      sinceRef.current = data.server_time;
      if (baseline) return;
      if (data.won_deals?.length) celebrate(data.won_deals, data);
      const tickets = (data.new_tickets || []).map((t) => ({ ...t, kind: "ticket" }));
      const replies = (data.new_messages || []).map((t) => ({ ...t, kind: "reply" }));
      pushPops(
        [...tickets, ...replies].map((t) => ({
          ...t, isDM: t.channel === "circle_dm", _k: `${t.id}-${Math.random().toString(36).slice(2, 8)}`,
        }))
      );
    } catch {}
  }, [celebrate, pushPops]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [poll]);

  const closeWon = useCallback(() => { setWonDeal(null); stopCelebrationSound(); }, []);

  return (
    <>
      {/* Won-deal celebration modal */}
      <Dialog open={!!wonDeal} onOpenChange={(o) => !o && closeWon()}>
        <DialogContent className="sm:max-w-md overflow-hidden border-0 p-0 bg-[#0b1f4d] text-white" data-testid="deal-won-modal">
          <div className="relative px-8 py-10 text-center">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/20 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500/20 flex items-center justify-center animate-bounce">
                <Trophy className="h-10 w-10 text-amber-300" />
              </div>
              <p className="mt-5 text-sm font-semibold uppercase tracking-widest text-emerald-300">Deal gewonnen!</p>
              <h2 className="mt-1 font-heading text-3xl font-extrabold">
                {wonDeal?.value ? fmtEUR(wonDeal.value) : "🎉"}
              </h2>
              <p className="mt-2 text-blue-100">
                {wonDeal?.contact_name || "Nieuwe klant"}
                {wonDeal?.product ? ` · ${wonDeal.product}` : ""}
              </p>
              <div className="mt-7 flex justify-center gap-2">
                <Button data-testid="deal-won-view-btn"
                  onClick={() => { const id = wonDeal?.id; closeWon(); if (id) navigate(`/sales/deals/${id}`); }}
                  className="bg-emerald-500 hover:bg-emerald-600 gap-2">
                  <PartyPopper className="h-4 w-4" /> Bekijk deal
                </Button>
                <Button data-testid="deal-won-close-btn" variant="ghost" onClick={closeWon}
                  className="text-blue-100 hover:bg-white/10 hover:text-white">Sluiten</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Incoming ticket / DM popups */}
      <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-2 w-80 max-w-[calc(100vw-2.5rem)]" data-testid="ticket-pops">
        {pops.map((t) => {
          const dest = t.kind === "reply" ? "/support/open" : "/support/nieuw";
          const heading = t.isDM
            ? (t.kind === "reply" ? "Nieuw DM-bericht" : "Nieuwe DM")
            : (t.kind === "reply" ? "Nieuw bericht" : "Nieuw support-ticket");
          const close = (e) => { e?.stopPropagation?.(); setPops((p) => p.filter((x) => x._k !== t._k)); };
          return (
            <div key={t._k} data-testid={`ticket-pop-${t.id}`}
              onClick={() => { close(); navigate(dest); }}
              className={`group cursor-pointer rounded-xl border bg-white shadow-xl p-3.5 flex gap-3 animate-in slide-in-from-right-8 fade-in duration-300 ${t.isDM ? "border-violet-200 ring-1 ring-violet-100" : "border-slate-200"}`}>
              <div className="relative shrink-0">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${t.isDM ? "bg-violet-100 text-violet-700 animate-bounce" : "bg-blue-100 text-blue-700"}`}>
                  {t.isDM ? <MessageCircle className="h-5 w-5" /> : <LifeBuoy className="h-5 w-5" />}
                </div>
                <span className={`absolute -top-1 -right-1 flex h-3 w-3 ${t.isDM ? "" : "hidden"}`}>
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-violet-500" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-xs font-semibold ${t.isDM ? "text-violet-700" : "text-blue-700"}`}>{heading}</p>
                  <button data-testid={`ticket-pop-close-${t.id}`} onClick={close}
                    className="text-slate-300 hover:text-slate-500"><X className="h-3.5 w-3.5" /></button>
                </div>
                <p className="text-sm font-medium text-slate-800 truncate">
                  {t.kind === "reply" ? (t.preview || t.subject || "(nieuw bericht)") : (t.subject || "(geen onderwerp)")}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {(t.contact_name || t.contact_email || "Onbekend")} · {CHANNEL_LABEL[t.channel] || t.channel}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
